import React, { useState } from "react";
import {
    Flex,
    Box,
    Text,
    useToast
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router";
import { SignUp } from "../../components/SignUp";
import { validateEmail, validatePassword, validatePhone } from "../../utils/helper";
import { useMutation, useLazyQuery } from "@apollo/client";
import { EDSHOP_SIGNUP } from "../../graphql/mutations";
import { CHECKDOMAINNAME } from "../../graphql/queries";
import { HelmetComponent } from "../../components/HelmetComponent";

const MotionBox = motion(Box);

export const SignUpPage = () => {
    const [countryCode, setCountryCode] = useState("+91");
    const [showCountryCode, setShowCountryCode] = useState(false);
    const [singUpForm, setSignUpForm] = useState({
        username: "",
        domainName: "",
        publisherName: "",
        firstName: "",
        lastName: "",
    })
    const [error, setError] = useState({
        message: "",
        isError: false,
        isSignUpError: false,
    })
    const [passwordError, setPasswordError] = useState({
        isPasswordError: false,
        isConfirmPasswordError: false
    })
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const toast = useToast();

    const [signUpUser] = useMutation(EDSHOP_SIGNUP, {
        onCompleted: (data) => {
            setIsLoading(false);
            navigate(`/verification-sent/?email=${singUpForm?.username}`)
        },
        onError: (err) => {
            setIsLoading(false);
            toast({
                position: "top-right",
                title: err.message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    })

    const [checkDomainName] = useLazyQuery(CHECKDOMAINNAME, {
        onCompleted: (data) => {
            if(data.edshopifyCheckDomainName) {
                signUpUser({
                    variables: {
                        data: {
                            firstName: singUpForm?.firstName,
                            lastName: singUpForm?.lastName,
                            publisherName: singUpForm?.publisherName,
                            domainName: singUpForm?.domainName,
                            password: singUpForm?.password,
                            ...(singUpForm?.username && isNaN(singUpForm?.username) ? { email: singUpForm?.username } : { phone: `${countryCode}${singUpForm?.username}` }),
                        }
                    }
                })
            } else {
                toast({
                    position: "top-right",
                    title: "Domain name already used. Please use a different domain name",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
                setIsLoading(false);
            }
        },
        onError: (err) => {
            setIsLoading(false);
        }
    })

    const handleFormChange = (e) => {
        let { name, value } = e.target
        const validations = {
            username: () => {
                if (isNaN(value)) {
                    setShowCountryCode(false);
                    return validateEmail(value) ? null : "Enter a valid email";
                } else {
                    if (value !== "") setShowCountryCode(true);
                    else setShowCountryCode(false);
                    return validatePhone(value) ? null : value === "" ? "Enter a valid email" : "Enter a valid email"; //Keep the logic for future
                }
            },
            password: () => {
                return validatePassword(value) ? null : "Invalid password format";
            },
            confirmPassword: () => {
                return value === singUpForm.password ? null : "Passwords do not match";
            },
        };

        const errorMessage = validations[name]?.();

        if (name === "username") {
            setError({
                ...error,
                isError: !!errorMessage,
                message: errorMessage || "",
            });
        }

        if (name === "password" || name === "confirmPassword") {
            setPasswordError({
                ...passwordError,
                ...(name === "password" ? { isPasswordError: !!errorMessage } : {}),
                ...(name === "confirmPassword" ? { isConfirmPasswordError: !!errorMessage } : {}),
            });
        }
        if(name === "domainName") {
            value = value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase();
        }
        setSignUpForm({
            ...singUpForm,
            [name]: value,
        });
    }

    const signUp = (e) => {
        e.preventDefault()
        if (isNaN(singUpForm?.username && !validateEmail(singUpForm?.username))) {
            setError({
                ...error,
                isError: true,
                message: "Enter a valid email",
            });
            return true;
        }
        if (!isNaN(singUpForm?.username) && !validatePhone(singUpForm?.username)) {
            setError({
                ...error,
                isError: true,
                message: "Enter a valid phone number",
            });
            return true;
        }
        setIsLoading(true);
        checkDomainName({
            variables: {
                where: {
                    domainName: singUpForm?.domainName
                }
            }
        }) 
    }

    return (
        <>
            <HelmetComponent title={`SignUp - Edshopify`}/>
            <Flex
                sx={{
                    minHeight: "100vh",
                    alignItems: { base: "flex-start", sm: "center" },
                    padding: "20px"
                }}
            >
                <Box sx={{width: "100%"}}>
                    <Text className='logo' sx={{margin: "0 0 20px", textAlign: "center"}}>EdShopify</Text>
                    <MotionBox
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        maxW="md"
                        mx="auto"
                        p={{ base: 0, sm: 6 }}
                        borderWidth={{ base: "none", sm: "1" }}
                        borderRadius={{ base: "none", sm: "lg" }}
                        shadow={{ base: "none", sm: "lg" }}
                        sx={{
                            minWidth: { base: "100%", sm: "400px" }
                        }}
                    >
                        <SignUp
                            singUpForm={singUpForm}
                            showCountryCode={showCountryCode}
                            error={error}
                            passwordError={passwordError}
                            handleFormChange={handleFormChange}
                            codeChange={(code) => setCountryCode(code)}
                            signUp={signUp}
                            isLoading={isLoading}
                        />
                    </MotionBox>
                </Box>
            </Flex>
        </>
    );
}